import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { MyOrdersService } from '../../features/my-orders/my-orders.service';
import { ToastFeedbackService } from '../../shared/services/toast-feedback.service';
import { NotificationsService } from './notifications.service';

/**
 * PushService — Manages FCM push notifications via Capacitor plugins.
 *
 * Platform strategy (Req 4.1 / 4.2a):
 * - **Android**: uses `@capacitor/push-notifications`, whose `registration`
 *   event already yields an FCM token (Firebase is wired via
 *   google-services.json in the native Android project).
 * - **iOS**: uses `@capacitor-firebase/messaging`, which returns a real **FCM
 *   token** (`getToken()`). The stock `@capacitor/push-notifications` would only
 *   give the raw APNs device token on iOS, which the backend (FCM HTTP v1)
 *   cannot use. Requires `GoogleService-Info.plist` + an APNs Auth Key uploaded
 *   to Firebase.
 *
 * The backend contract is identical for both: POST /v1/devices/register with
 * `{ deviceToken: <fcmToken>, platform }`. The `platform` is derived at runtime.
 *
 * On login: request permission (in context), get FCM token, register.
 * On push received (foreground): auto-refresh orders + persist locally + toast.
 * On tap: navigate to WO detail.
 * On logout: DELETE /devices + remove listeners.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly myOrders = inject(MyOrdersService);
  private readonly toastFeedback = inject(ToastFeedbackService);
  private readonly notificationsService = inject(NotificationsService);

  private readonly apiUrl = environment.apiUrl;
  private currentToken: string | null = null;
  private listenersAttached = false;

  /** True on iOS native, where we use @capacitor-firebase/messaging for a real FCM token. */
  private get useFirebaseMessaging(): boolean {
    return Capacitor.getPlatform() === 'ios';
  }

  /**
   * Requests the push permission and registers the device.
   *
   * MUST be called **in context** (after a successful login / onboarding),
   * NOT on cold start. On iOS the native push dialog is shown only once: if the
   * user denies it on cold start, the app cannot re-ask without sending them to
   * Settings (Req 4.1b, ASRG 5.1.1). For cold-start re-registration of an
   * already-granted permission, use `resumeIfRegistered()` instead.
   */
  async initialize(): Promise<void> {
    try {
      if (this.useFirebaseMessaging) {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        const permResult = await FirebaseMessaging.requestPermissions();
        if (permResult.receive !== 'granted') return;
        await this.attachListenersAndRegister();
        return;
      }

      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') return;
      await this.attachListenersAndRegister();
    } catch (error) {
      console.warn('[Push] Init failed:', error);
    }
  }

  /**
   * Cold-start safe re-registration: re-attaches listeners and re-registers the
   * device ONLY if the push permission was already granted previously. Does NOT
   * trigger the native permission dialog (Req 4.1b). Call this from the app
   * initializer when a session is restored.
   */
  async resumeIfRegistered(): Promise<void> {
    try {
      if (this.useFirebaseMessaging) {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        const perm = await FirebaseMessaging.checkPermissions();
        if (perm.receive !== 'granted') return; // never prompt on cold start
        await this.attachListenersAndRegister();
        return;
      }

      const perm = await PushNotifications.checkPermissions();
      if (perm.receive !== 'granted') return; // never prompt on cold start
      await this.attachListenersAndRegister();
    } catch (error) {
      console.warn('[Push] Resume failed:', error);
    }
  }

  private async attachListenersAndRegister(): Promise<void> {
    try {
      if (!this.listenersAttached) {
        await this.attachListeners();
        this.listenersAttached = true;
      }

      if (this.useFirebaseMessaging) {
        // Firebase Messaging: fetch the FCM token directly. The `tokenReceived`
        // listener (attached above) also fires on token refresh.
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        const { token } = await FirebaseMessaging.getToken();
        if (token) {
          this.currentToken = token;
          await this.registerDevice(token);
        }
        void this.captureDeliveredNotifications();
        return;
      }

      // Android: register() triggers the `registration` event with the FCM token.
      await PushNotifications.register();
      void this.captureDeliveredNotifications();
    } catch (error) {
      console.warn('[Push] Register failed:', error);
    }
  }

  private async attachListeners(): Promise<void> {
    if (this.useFirebaseMessaging) {
      await this.attachFirebaseListeners();
      return;
    }
    await this.attachAndroidListeners();
  }

  /** iOS listeners via @capacitor-firebase/messaging. */
  private async attachFirebaseListeners(): Promise<void> {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      // Token refresh — re-register with the new FCM token.
      if (event.token && event.token !== this.currentToken) {
        this.currentToken = event.token;
        void this.registerDevice(event.token);
      }
    });

    await FirebaseMessaging.addListener('notificationReceived', (event) => {
      const n = event.notification;
      console.info('[Push] Received (iOS):', n.title);
      void this.myOrders.refresh();

      const data = (n.data ?? undefined) as Record<string, string> | undefined;
      void this.notificationsService.addNotification({
        title: n.title ?? 'Notificación',
        body: n.body ?? '',
        type: data?.['action'] ?? 'general',
        workOrderId: data?.['workOrderId'],
        data: data ?? undefined,
      });

      if (n.title) {
        void this.toastFeedback.showSuccess(`${n.title}: ${n.body ?? ''}`);
      }

      if (data?.['type'] === 'eta_notification_sent') {
        const minutes = parseInt(data['estimatedMinutes'] ?? '0', 10);
        void this.toastFeedback.showEtaNotificationSent(minutes || 10);
      }
    });

    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const n = event.notification;
      const data = (n.data ?? undefined) as Record<string, string> | undefined;
      const workOrderId = data?.['workOrderId'];

      void this.notificationsService.addNotification({
        title: n.title ?? 'Notificación',
        body: n.body ?? '',
        type: data?.['action'] ?? 'general',
        workOrderId: workOrderId,
        data: data ?? undefined,
      });

      if (workOrderId) {
        void this.myOrders.refresh();
        void this.router.navigate(['/orders', workOrderId]);
      }
    });
  }

  /** Android listeners via @capacitor/push-notifications (unchanged behavior). */
  private async attachAndroidListeners(): Promise<void> {
    await PushNotifications.addListener('registration', (token: Token) => {
      this.currentToken = token.value;
      void this.registerDevice(token.value);
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.info('[Push] Received:', notification.title);
        // Auto-refresh orders when a push arrives in foreground
        void this.myOrders.refresh();

        // Persist notification locally for in-app notification center
        const data = notification.data as Record<string, string> | undefined;
        void this.notificationsService.addNotification({
          title: notification.title ?? 'Notificación',
          body: notification.body ?? '',
          type: data?.['action'] ?? 'general',
          workOrderId: data?.['workOrderId'],
          data: data ?? undefined,
        });

        // Show toast for foreground notifications so user knows a push arrived
        if (notification.title) {
          void this.toastFeedback.showSuccess(
            `${notification.title}: ${notification.body ?? ''}`,
          );
        }

        // GPS v2: Show ETA toast when technician's customer was notified
        if (data?.['type'] === 'eta_notification_sent') {
          const minutes = parseInt(data['estimatedMinutes'] ?? '0', 10);
          void this.toastFeedback.showEtaNotificationSent(minutes || 10);
        }
      },
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        const data = action.notification.data as Record<string, string> | undefined;
        const workOrderId = data?.['workOrderId'];

        // Persist notification that arrived while app was in background
        void this.notificationsService.addNotification({
          title: action.notification.title ?? 'Notificación',
          body: action.notification.body ?? '',
          type: data?.['action'] ?? 'general',
          workOrderId: workOrderId,
          data: data ?? undefined,
        });

        if (workOrderId) {
          // Refresh orders list then navigate to the specific order
          void this.myOrders.refresh();
          void this.router.navigate(['/orders', workOrderId]);
        }
      },
    );
  }

  /**
   * Reads notifications delivered to the system tray while the app was closed.
   * Persists them locally and clears the system tray.
   * Call this when the app resumes or the orders page loads.
   */
  async captureDeliveredNotifications(): Promise<void> {
    try {
      if (this.useFirebaseMessaging) {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        const { notifications } = await FirebaseMessaging.getDeliveredNotifications();
        for (const n of notifications) {
          const data = (n.data ?? undefined) as Record<string, string> | undefined;
          await this.notificationsService.addNotification({
            title: n.title ?? 'Notificación',
            body: n.body ?? '',
            type: data?.['action'] ?? 'general',
            workOrderId: data?.['workOrderId'],
            data: data ?? undefined,
          });
        }
        if (notifications.length > 0) {
          await FirebaseMessaging.removeAllDeliveredNotifications();
        }
        return;
      }

      const { notifications } = await PushNotifications.getDeliveredNotifications();
      for (const notification of notifications) {
        const data = notification.data as Record<string, string> | undefined;
        await this.notificationsService.addNotification({
          title: notification.title ?? 'Notificación',
          body: notification.body ?? '',
          type: data?.['action'] ?? 'general',
          workOrderId: data?.['workOrderId'],
          data: data ?? undefined,
        });
      }
      if (notifications.length > 0) {
        await PushNotifications.removeAllDeliveredNotifications();
      }
    } catch {
      // Best-effort — some platforms don't support getDeliveredNotifications
    }
  }

  private async registerDevice(token: string): Promise<void> {
    try {
      // Derive platform at runtime instead of hardcoding 'android' (Req 4.2).
      // Capacitor.getPlatform() returns 'ios' | 'android' | 'web'.
      const platform = Capacitor.getPlatform();
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/v1/devices/register`, {
          deviceToken: token,
          platform,
        }),
      );
    } catch (error) {
      console.warn('[Push] Registration failed:', error);
    }
  }

  async unregister(): Promise<void> {
    try {
      await firstValueFrom(this.http.delete(`${this.apiUrl}/v1/devices`));
      if (this.useFirebaseMessaging) {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        await FirebaseMessaging.removeAllListeners();
      } else {
        await PushNotifications.removeAllListeners();
      }
      this.listenersAttached = false;
      this.currentToken = null;
    } catch { /* best-effort */ }
  }
}
